/* PDP v3 delivery ladder — the status line under the add-to-cart button.
 * Stage 4 of agents/plans/2026-07-06-pdp-v3-size-finder-section-plan.md.
 *
 * At rest it shows the Liquid-generated per-market resting copy ("FREE
 * delivery across Australia on orders $59+"). Once the cart has items it
 * flips to the tiered nudge from the v3 demo (confirmed content decision):
 * lowercase voice, branded FREE terms keep their caps, and a whole-unit ask
 * ("add 1 more") is preferred over a dollar amount when one more item of the
 * current product crosses the next tier.
 *
 * Thresholds arrive in cents via data attributes: both tiers are resolved
 * per visitor country in Liquid (snippets/toshi-shipping-threshold, reading
 * the free_shipping_amounts / express_shipping_amounts theme settings shared
 * with the cart drawer); the section renders no ladder at all when the
 * visitor's country has no free tier, so this element only ever exists with
 * freeAt > 0. The resting cart total is seeded from Liquid (data-cart-total),
 * so no request is made until the cart actually changes; after that, totals
 * come from /cart.js on the theme's global on:cart:after-merge event. That
 * event fires several times per cart change, so fetches are single-flight:
 * a burst coalesces into at most one trailing re-fetch, and renders always
 * reflect the newest response.
 */
(() => {
  class DeliveryLadder extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.freeAt = parseInt(this.dataset.freeAt, 10) || 0;
      this.expressAt = parseInt(this.dataset.expressAt, 10) || 0;
      this.itemPrice = parseInt(this.dataset.itemPrice, 10) || 0;
      this.lastTotal = parseInt(this.dataset.cartTotal, 10) || 0;
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this.textEl = this.querySelector('[data-status-line-text]');
      this.dotEl = this.querySelector('.pdp-statusline__dot');
      this.restHtml = this.textEl ? this.textEl.innerHTML : '';

      this.boundRefresh = () => this.refresh();
      document.addEventListener('on:cart:after-merge', this.boundRefresh);

      // the "add 1 more" maths follow whichever variant is selected
      this.productForm = this.closest('product-form');
      this.boundVariantChange = (evt) => {
        const price = evt.detail && evt.detail.variant && evt.detail.variant.price;
        if (typeof price === 'number') {
          this.itemPrice = price;
          this.render(this.lastTotal);
        }
      };
      this.productForm?.addEventListener('on:variant:change', this.boundVariantChange);

      this.render(this.lastTotal);
    }

    disconnectedCallback() {
      document.removeEventListener('on:cart:after-merge', this.boundRefresh);
      this.productForm?.removeEventListener('on:variant:change', this.boundVariantChange);
    }

    async refresh() {
      if (this.fetching) {
        this.refetchQueued = true;
        return;
      }
      this.fetching = true;
      try {
        do {
          this.refetchQueued = false;
          const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`);
          if (!res.ok) break;
          const cart = await res.json();
          this.lastTotal = cart.total_price;
          this.render(this.lastTotal);
        } while (this.refetchQueued);
      } catch (e) {
        /* leave the current copy in place */
      } finally {
        this.fetching = false;
      }
    }

    /* Format cents through the store's money format (data-money-format,
     * e.g. "${{amount}}"). Whole-dollar amounts drop the ".00" — ladder
     * copy reads "add $12.50", not "add $12.50 AUD 0.00c". */
    money(cents) {
      const amount = (cents / 100).toFixed(2).replace(/\.00$/, '');
      return this.moneyFormat.replace(/\{\{\s*amount[a-z_]*\s*\}\}/, amount);
    }

    render(total) {
      if (!this.textEl) return;
      let state = 'info';
      let html = this.restHtml;

      if (total > 0 && this.freeAt > 0) {
        const oneMore = (remaining) => this.itemPrice > 0 && remaining <= this.itemPrice;
        if (this.expressAt > 0 && total >= this.expressAt) {
          state = 'go';
          html = '<b>FREE Express delivery</b> on this order';
        } else if (total >= this.freeAt) {
          if (this.expressAt > 0) {
            const remaining = this.expressAt - total;
            state = 'go';
            html = oneMore(remaining)
              ? '<b>FREE delivery</b> &middot; add 1 more for <b>FREE Express</b>'
              : `<b>FREE delivery</b> &middot; add ${this.money(remaining)} for <b>FREE Express</b>`;
          } else {
            state = 'go';
            html = '<b>FREE delivery</b> on this order';
          }
        } else {
          const remaining = this.freeAt - total;
          state = 'up';
          html = oneMore(remaining)
            ? '<b>good choice!</b> add 1 more for <b>FREE delivery</b>'
            : `<b>good choice!</b> add ${this.money(remaining)} for <b>FREE delivery</b>`;
        }
      }

      this.dataset.state = state;
      this.textEl.innerHTML = html;
      if (this.dotEl) this.dotEl.textContent = state === 'go' ? '✓' : '';
    }
  }

  if (!window.customElements.get('delivery-ladder')) {
    window.customElements.define('delivery-ladder', DeliveryLadder);
  }
})();
